import api from './axios';

export interface Course {
    id: string;
    code: string;
    name: string;
    description?: string;
    credits: number;
    hours: number;
    semester: string[];
    year: '1' | '2' | '3' | '4';
    status: 'actif' | 'inactif' | 'archive';
    currentStudents: number;
    teacher?: { id: string; firstName: string; lastName: string };
    createdAt: Date;
    updatedAt: Date;
}

export interface CreateCourseData {
    code?: string;
    name: string;
    description?: string;
    credits: number;
    hours: number;
    semester: string[];
    year: '1' | '2' | '3' | '4';
    status?: 'actif' | 'inactif' | 'archive';
}

export interface UpdateCourseData extends Partial<CreateCourseData> { }

export interface CourseFilters {
    semester?: string;
    year?: string;
    status?: string;
}

export interface CourseStats {
    total: number;
    active: number;
    totalHours: number;
    totalCredits: number;
    bySemester: { semester: string; count: number }[];
    byYear: { year: string; count: number }[];
    byStatus: { status: string; count: number }[];
}

// Get all courses with optional filters
export const getCourses = async (filters?: CourseFilters): Promise<Course[]> => {
    const params = new URLSearchParams();
    if (filters?.semester) params.append('semester', filters.semester);
    if (filters?.year) params.append('year', filters.year);
    if (filters?.status) params.append('status', filters.status);

    const response = await api.get(`/courses?${params.toString()}`);
    return response.data.map((course: any) => ({
        ...course,
        // Parse semester: TypeORM simple-array returns string, convert to array
        semester: typeof course.semester === 'string'
            ? course.semester.split(',').filter(Boolean)
            : Array.isArray(course.semester)
                ? course.semester
                : [],
        createdAt: new Date(course.createdAt),
        updatedAt: new Date(course.updatedAt),
    }));
};

// Get course by ID
export const getCourseById = async (id: string): Promise<Course> => {
    const response = await api.get(`/courses/${id}`);
    return {
        ...response.data,
        semester: typeof response.data.semester === 'string'
            ? response.data.semester.split(',').filter(Boolean)
            : Array.isArray(response.data.semester)
                ? response.data.semester
                : [],
        createdAt: new Date(response.data.createdAt),
        updatedAt: new Date(response.data.updatedAt),
    };
};

// Get course by code
export const getCourseByCode = async (code: string): Promise<Course> => {
    const response = await api.get(`/courses/code/${code}`);
    return {
        ...response.data,
        createdAt: new Date(response.data.createdAt),
        updatedAt: new Date(response.data.updatedAt),
    };
};

// Create a new course
export const createCourse = async (data: CreateCourseData): Promise<Course> => {
    const response = await api.post('/courses', data);
    return {
        ...response.data,
        createdAt: new Date(response.data.createdAt),
        updatedAt: new Date(response.data.updatedAt),
    };
};

// Update a course
export const updateCourse = async (
    id: string,
    data: UpdateCourseData
): Promise<Course> => {
    const response = await api.patch(`/courses/${id}`, data);
    return {
        ...response.data,
        createdAt: new Date(response.data.createdAt),
        updatedAt: new Date(response.data.updatedAt),
    };
};

// Delete a course
export const deleteCourse = async (id: string): Promise<void> => {
    await api.delete(`/courses/${id}`);
};

// Get course statistics
export const getCourseStats = async (): Promise<CourseStats> => {
    const response = await api.get('/courses/stats');
    return response.data;
};
